import type { QueryClient } from "@tanstack/react-query";
import { sendQuotationChatMsg } from "./api/createQuotationChat";
import type { Message as MessageType } from "./components/Message/types";

export async function SSEChatClient(
  sourceEntity: string,
  userName: string,
  userEmail: string,
  chatSessionUid: string,
  inputValue: string,
  setMessage: (message: string, chatSessionUid: string) => void,
  setSSEDone: () => void,
) {
  let response: Response | null = null;
  try {
    response = await sendQuotationChatMsg({
      message: inputValue,
      chat_session_uid: chatSessionUid,
      source_entity: sourceEntity,
      username: userName,
      user_email: userEmail,
    });
  } catch {
    setSSEDone();
    return;
  }

  if (!response) {
    setSSEDone();
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    setSSEDone();
    return;
  }

  const decoder = new TextDecoder("utf-8");

  while (true) {
    try {
      const { value, done } = await reader.read();

      if (done) {
        setSSEDone();
        break;
      }

      const text = decoder.decode(value, { stream: true });
      const textArray = text.split("\n");
      for (const line of textArray) {
        if (line.trim() === "") continue;
        if (line.startsWith("data:") || line.startsWith("event:")) continue;
        try {
          const json = JSON.parse(line);
          setMessage(json.content, json.chat_session_uid);
        } catch {
          setSSEDone();
          break;
        }
      }
    } catch {
      setSSEDone();
      break;
    }
  }
}

/**
 * Creates a stream message handler for updating messages in real-time during SSE streaming.
 * @param currentSessionId - The initial session ID when the message was sent
 * @param placeholderMessageId - The ID of the placeholder message to update
 * @param queryClient - React Query client for cache updates
 * @param setSelectedSessionId - State setter for updating the selected session ID
 * @param actualSessionIdRef - Mutable reference to track the actual session ID (may change during streaming)
 * @returns A callback function to handle streamed messages
 */
export function createStreamMessageHandler(
  currentSessionId: string,
  placeholderMessageId: string,
  queryClient: QueryClient,
  setSelectedSessionId: (id: string) => void,
  actualSessionIdRef: { current: string },
) {
  return (streamedMessage: string, chatSessionUid: string) => {
    // Update selected session ID if it changes
    if (chatSessionUid !== currentSessionId) {
      setSelectedSessionId(chatSessionUid);
      actualSessionIdRef.current = chatSessionUid;
    }

    // Update the placeholder message in real-time as SSE streams
    queryClient.setQueryData<{ data: { messages: MessageType[] } }>(
      ["chatMessages", chatSessionUid || actualSessionIdRef.current],
      (oldData) => {
        if (!oldData) return oldData;

        const updatedMessages = oldData.data.messages.map((msg) => {
          if (msg.id === placeholderMessageId) {
            return {
              ...msg,
              content: streamedMessage,
              chat_session_uid: chatSessionUid || msg.chat_session_uid,
            };
          }
          return msg;
        });

        return {
          ...oldData,
          data: { ...oldData.data, messages: updatedMessages },
        };
      },
    );
  };
}
